import { ReactElement, useRef, useState } from "react";
import useIsMobile from "components/hooks/useIsMobile";
import { Box, Typography, Card, CardContent, TextField } from "@mui/material";
import { ChoiceSelections } from "./../types";
import { StepHeader } from "../components/StepHeader";
import { StepImagePanel } from "../components/StepImagePanel";
import { NextStepButton } from "./NextStepButton";
import Checkbox from "components/common/Checkbox";

export type Choice = {
  id: string;
  header?: string;
  text: string;
  icon?: ReactElement;
  freeform?: boolean;
  fullWidth?: boolean;
  freeformPlaceholder?: string;
  isSingleChoice?: boolean;
};

const cardSx = (selected: boolean, fullWidth?: boolean) => {
  return {
    cursor: "pointer",
    bgcolor: selected ? "#198282" : "white",
    color: selected ? "white" : "inherit",
    transition: "background-color 0.3s",
    borderRadius: "16px",
    border: "1px solid #EAEBEC",
    boxShadow: "none",
    gridColumn: fullWidth ? "1 / -1" : "",

    "& svg": { color: selected ? "white" : "#8E9598" },

    "&:hover": {
      bgcolor: selected ? "#276767" : "white",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    },
  };
};

const cardContentSx = (header: boolean) => {
  const isMobile = useIsMobile();
  return {
    display: "flex",
    flexDirection: header ? "row" : "column",
    alignItems: header ? "start" : "center",
    alignSelf: "center",
    height: "100%",
    justifyContent: "center",
    gap: !header ? "0px" : "12px",
    padding: isMobile ? "20px 12px !important" : "24px",
  };
};

const detailedCardSx = (selected: boolean, fullWidth?: boolean) => {
  return {
    cursor: "pointer",
    bgcolor: "white",
    borderRadius: "12px",
    border: "1px solid",
    borderColor: selected ? "primary.main" : "#EAEBEC",
    boxShadow: "none",
    transition: "border-color 0.2s",
    gridColumn: fullWidth ? "1 / -1" : "",

    "&:hover": {
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    },
  };
};

const DetailedChoiceCard = ({
  choice,
  value,
  onSelected,
  fullWidth,
}: {
  choice: Choice;
  value: boolean;
  onSelected: (v: boolean) => void;
  fullWidth?: boolean;
}) => {
  const isMobile = useIsMobile();

  return (
    <Card
      onClick={() => onSelected(!value)}
      sx={detailedCardSx(value, fullWidth)}
    >
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: isMobile ? "12px" : "16px",
          padding: isMobile ? "12px 14px !important" : "20px !important",
        }}
      >
        {choice.icon && (
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: isMobile ? "44px" : "56px",
              height: isMobile ? "44px" : "56px",
              borderRadius: "50%",
              backgroundColor: "teal.2",
              "& svg": {
                width: isMobile ? "22px" : "26px",
                height: isMobile ? "22px" : "26px",
                color: "primary.main",
              },
            }}
          >
            {choice.icon}
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "2px",
            flex: 1,
          }}
        >
          <Typography variant="bodyHeavy" sx={{ userSelect: "none" }}>
            {choice.header || choice.text}
          </Typography>
          {choice.header && (
            <Typography
              variant="bodySmall"
              sx={{ userSelect: "none", color: "text.secondary" }}
            >
              {choice.text}
            </Typography>
          )}
        </Box>
        {/* The card owns the click, so the box is display-only. */}
        <Checkbox
          checked={value}
          readOnly
          disableRipple
          sx={{ flexShrink: 0, padding: 0, pointerEvents: "none" }}
        />
      </CardContent>
    </Card>
  );
};

const ChoiceCard = ({
  choice,
  value,
  onSelected,
  fullWidth,
}: {
  choice: Choice;
  value: boolean;
  onSelected: (v: boolean) => void;
  fullWidth?: boolean;
}) => {
  return (
    <Card
      onClick={!choice.freeform ? () => onSelected(!value) : undefined}
      sx={cardSx(value, fullWidth)}
    >
      <CardContent sx={cardContentSx(choice.header != null)}>
        <Box sx={{ width: "24px", "& svg": { width: "24px" } }}>
          {choice.icon}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          {choice.header && (
            <Typography variant="bodyHeavy" color="inherit">
              {choice.header}
            </Typography>
          )}
          <Typography
            variant="body"
            sx={{
              userSelect: "none",
              color: "inherit",
              textAlign: !choice.header ? "center" : "",
            }}
          >
            {choice.text}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const FreeformChoiceCard = ({
  choice,
  value,
  fullWidth = false,
  onSelected,
}: {
  choice: Choice;
  value?: string;
  fullWidth?: boolean;
  onSelected: (v: boolean | string) => void;
}) => {
  const [freeformValue, setFreeformValue] = useState(value || "");
  const textFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const handleBlur = () => {
    onSelected(freeformValue.trim() || false);
    setHighlighted(freeformValue.trim().length > 0);
  };
  const [highlighted, setHighlighted] = useState(!!value);

  return (
    <Card
      onClick={() => textFieldRef.current?.focus()}
      sx={cardSx(highlighted, fullWidth)}
    >
      <CardContent sx={cardContentSx(choice.header != null)}>
        {choice.icon}
        <TextField
          fullWidth
          multiline
          inputRef={textFieldRef}
          variant="standard"
          placeholder={choice.text}
          defaultValue={value ? value : ""}
          onChange={(e) => {
            setFreeformValue(e.target.value);
            onSelected(e.target.value);
          }}
          onBlur={handleBlur}
          InputProps={{
            disableUnderline: true,
          }}
          sx={{
            cursor: "pointer",

            "& .MuiInputBase-root": {
              color: "inherit",
              fontSize: "1rem",
              fontWeight: "500",
              "& textarea": { textAlign: "center" },
            },
          }}
        />
      </CardContent>
    </Card>
  );
};

export interface Props {
  title: string;
  subtitle: string;
  onNext: (choices: ChoiceSelections) => Promise<void>;
  choiceOptions: Choice[];
  columns?: number;
  initialChoices?: ChoiceSelections;
  disabled: boolean;
  skippable?: boolean;
  singleSelectStep?: boolean;
  img?: string;
  mobileImg?: string;
  cardVariant?: "default" | "detailed";
  imageHeight?: string;
  buttonText?: string;
}

export default ({
  title,
  subtitle,
  onNext,
  choiceOptions,
  columns = 2,
  initialChoices,
  disabled,
  skippable,
  singleSelectStep = false,
  img,
  mobileImg,
  cardVariant = "default",
  imageHeight,
  buttonText,
}: Props) => {
  const [selected, setSelected] = useState<ChoiceSelections>(
    initialChoices || {},
  );
  const [loading, setLoading] = useState(false);

  const isMobile = useIsMobile();
  const hasFullWidthChoice = choiceOptions.filter((choice) => choice.fullWidth);

  const handleSelect = (
    id: string,
    v: string | boolean,
    isSingleChoice: boolean = false,
  ) => {
    setSelected((prev) => {
      if (isSingleChoice) {
        return { [id]: v };
      }
      const newSelection = { ...prev };

      // Check for any options with isSingleChoice
      choiceOptions.forEach((option) => {
        if ((singleSelectStep || option.isSingleChoice) && option.id !== id) {
          // Remove the option from the selection if it is not the option being selected
          delete newSelection[option.id];
        }
      });

      if (!v) {
        delete newSelection[id];
      } else {
        newSelection[id] = v;
      }
      return newSelection;
    });
  };
  const stackedLayout = choiceOptions[0].header && isMobile;
  const handleContinue = async () => {
    setLoading(true);
    try {
      await onNext(selected);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const choiceGrid = (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: stackedLayout
          ? "repeat(1, 1fr)"
          : isMobile
            ? "repeat(2, 1fr)"
            : `repeat(${columns}, 1fr)`,
        gap: isMobile ? "12px" : "24px",
        marginBottom: isMobile ? "90px" : "",
        alignItems: "stretch",
        width: "100%",
        maxWidth: "850px",
      }}
    >
      {choiceOptions.map((choice) =>
        choice.freeform ? (
          <FreeformChoiceCard
            key={choice.id}
            onSelected={(v) => handleSelect(choice.id, v)}
            value={
              choice.id in selected
                ? (selected[choice.id] as string)
                : undefined
            }
            choice={choice}
            fullWidth={choice.fullWidth}
          />
        ) : cardVariant === "detailed" ? (
          <DetailedChoiceCard
            key={choice.id}
            onSelected={(v) =>
              handleSelect(choice.id, v, choice.isSingleChoice)
            }
            value={!!selected[choice.id]}
            choice={choice}
            fullWidth={choice.fullWidth}
          />
        ) : (
          <ChoiceCard
            key={choice.id}
            onSelected={(v) =>
              handleSelect(choice.id, v, choice.isSingleChoice)
            }
            value={!!selected[choice.id]}
            choice={choice}
            fullWidth={choice.fullWidth}
          />
        ),
      )}

      {/* Continue button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: img ? "stretch" : "end",
          alignItems: "end",
          gridColumn:
            hasFullWidthChoice || choiceOptions.length % columns == 0
              ? "span " + columns
              : "auto",
          position: isMobile ? "fixed" : "relative",
          bottom: isMobile ? "0px" : "",
          width: isMobile ? "calc(100% - 32px)" : "auto",
          backgroundColor: "#FDFAF7",
          paddingBottom: isMobile ? "22px" : "0px",
        }}
      >
        <NextStepButton
          skippable={skippable}
          continueClick={handleContinue}
          disabled={loading || disabled}
          hasValue={Object.keys(selected).length > 0}
          buttonText={buttonText}
          fullWidth={!!img}
        />
      </Box>
    </Box>
  );

  if (img) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "center",
          alignItems: "center",
          gap: isMobile ? "12px" : "64px",
          padding: isMobile ? "" : "0 32px",
          paddingBottom: isMobile ? "24px" : "32px",
        }}
      >
        <StepImagePanel img={img} mobileImg={mobileImg} height={imageHeight} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            maxWidth: "650px",
            width: "100%",
            padding: isMobile ? "0 8px" : "",
          }}
        >
          <StepHeader title={title} subtitle={subtitle} alignment="left" />
          {choiceGrid}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "12px",
      }}
    >
      <StepHeader title={title} subtitle={subtitle} />
      {choiceGrid}
    </Box>
  );
};
